import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Tab, Tabs, Typography, useTheme,
} from '@mui/material';
import { ArrowBack, Business, DeleteOutline, EventAvailable, People, ReceiptLong, Inventory2, OpenInNew } from '@mui/icons-material';
import { apiClient } from '../services/api';
import { Company, CompanyAdminSummary, SubscriptionStatusDto, User } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { errorMessage } from '../utils/errorMessage';
import { DeleteCompanyDialog, SubscriptionDialog, CompanyNoteDialog } from '../features/admin';
import { UpdateSubscriptionRequest } from '../types';
import { formatCurrency } from '../utils/currency';
import { useCompanyMutations } from '../hooks/useCompanyMutations';
import PageHeader from '../components/common/PageHeader';
import PageHeaderActionButton from '../components/common/PageHeaderActionButton';
import MetricCard from '../components/common/MetricCard';
import StatusChip from '../components/common/StatusChip';

const dateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CompanyDetail: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const range = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { startDate: dateParam(start), endDate: dateParam(end) };
  }, []);

  const companyQuery = useQuery<Company>({
    queryKey: ['admin', 'company', companyId],
    queryFn: () => apiClient.getCompany(companyId!),
    enabled: Boolean(companyId),
  });
  const summaryQuery = useQuery<CompanyAdminSummary>({
    queryKey: ['admin', 'company', companyId, 'summary', range.startDate, range.endDate],
    queryFn: () => apiClient.getCompanyAdminSummary(companyId!, range.startDate, range.endDate),
    enabled: Boolean(companyId),
  });
  const usersQuery = useQuery<User[]>({
    queryKey: ['admin', 'company', companyId, 'users'],
    queryFn: () => apiClient.getUsersByCompany(companyId!),
    enabled: Boolean(companyId) && tab === 1,
  });
  const subscriptionQuery = useQuery<SubscriptionStatusDto>({
    queryKey: ['admin', 'company', companyId, 'subscription'],
    queryFn: () => apiClient.getCompanySubscription(companyId!),
    enabled: Boolean(companyId),
  });
  const { deleteCompanyMutation } = useCompanyMutations();

  if (companyQuery.isLoading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}><CircularProgress /></Box>;
  if (companyQuery.isError || !companyQuery.data) {
    return <Alert severity="error">{errorMessage(companyQuery.error, t('companies.detailLoadError'))}</Alert>;
  }
  const company = companyQuery.data;
  const summary = summaryQuery.data;
  const effectiveStatus = subscriptionQuery.data?.effectiveStatus;
  const statusLabel = effectiveStatus === 'ACTIVE' ? t('subscription.active') : effectiveStatus === 'SUSPENDED' ? t('subscription.suspended') : t('subscription.readOnly');

  const openSubscription = async () => {
    setSubscriptionError(null);
    setSubscriptionOpen(true);
    try { setSubscription(await apiClient.getCompanySubscription(company.id)); }
    catch (error: unknown) { setSubscriptionError(errorMessage(error, t('subscription.loadFailed'))); }
  };
  const saveSubscription = async (request: UpdateSubscriptionRequest) => {
    const updated = await apiClient.updateCompanySubscription(company.id, request);
    setSubscription(updated);
    await queryClient.invalidateQueries({ queryKey: ['admin'] });
    setSubscriptionOpen(false);
  };

  const confirmDelete = async () => {
    if (!company) return;
    try {
      setDeleteError(null);
      await deleteCompanyMutation.mutateAsync(company.id);
      navigate('/companies', { replace: true });
    } catch (error: unknown) {
      const rawMessage = errorMessage(error, '');
      const translatedMessage = rawMessage.includes('associated users')
        ? t('companies.deleteError.hasUsers')
        : rawMessage.includes('associated data')
          ? t('companies.deleteError.hasData')
          : rawMessage || t('companies.deleteError.generic');
      setDeleteError(translatedMessage);
      throw error;
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/companies')}>{t('common.back')}</Button>
      </Stack>
      <PageHeader
        title={company.name}
        description={`${company.email || '—'} · ${company.phone || '—'}`}
        icon={<Business />}
        accent="companies"
        actions={(
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            <StatusChip color={effectiveStatus === 'ACTIVE' ? 'success' : 'warning'} label={statusLabel} />
            <PageHeaderActionButton startIcon={<EventAvailable />} onClick={() => void openSubscription()}>{t('subscription.manage')}</PageHeaderActionButton>
            <PageHeaderActionButton color="error" startIcon={<DeleteOutline />} onClick={() => { setDeleteError(null); setDeleteOpen(true); }}>{t('companies.deleteCompany')}</PageHeaderActionButton>
          </Stack>
        )}
      />
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab label={t('companies.detailOverview')} />
        <Tab label={t('companies.detailUsers')} />
        <Tab label={t('companies.detailSubscription')} />
        <Tab label={t('companies.detailNotes')} />
      </Tabs>
      {tab === 0 && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('companies.detailOverview')}</Typography>
            <Typography variant="body2" color="text.secondary">{company.address || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">{company.website || '—'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('companies.createdAt')}: {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'}</Typography>
          </Paper>
          {summaryQuery.isError && <Alert severity="error">{errorMessage(summaryQuery.error, t('companies.summaryLoadError'))}</Alert>}
          {summary && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1.5 }}>
            {[
              [t('companies.usersCount'), String(summary.counts.userCount), <People fontSize="small" />, theme.palette.primary.main],
              [t('companies.customersCount'), String(summary.counts.customerCount), <People fontSize="small" />, theme.palette.info.main],
              [t('companies.itemsCount'), String(summary.counts.itemCount), <Inventory2 fontSize="small" />, theme.palette.success.main],
              [t('companies.periodSales'), formatCurrency(summary.period.totalSalesAmount), <ReceiptLong fontSize="small" />, theme.palette.secondary.main],
              [t('companies.periodCollections'), formatCurrency(summary.period.salesCollectionAmount), <ReceiptLong fontSize="small" />, theme.palette.warning.main],
            ].map(([label, value, icon, color]) => <MetricCard key={String(label)} title={String(label)} value={String(value)} icon={icon} color={String(color)} />)}
          </Box>}
          {summary && <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}><Typography variant="body2"><strong>{t('companies.lastActivity')}:</strong> {summary.activity.lastBusinessActivityAt ? new Date(summary.activity.lastBusinessActivityAt).toLocaleString() : t('companies.neverUsed')}</Typography></Paper>}
          <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('companies.companyOperations')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('companies.companyOperationsHint')}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button component="a" href={`/customers?companyId=${company.id}`} variant="outlined" startIcon={<OpenInNew />}>{t('companies.openCustomers')}</Button>
              <Button component="a" href={`/items?companyId=${company.id}`} variant="outlined" startIcon={<OpenInNew />}>{t('companies.openItems')}</Button>
              <Button component="a" href={`/sales?companyId=${company.id}`} variant="outlined" startIcon={<OpenInNew />}>{t('companies.openSales')}</Button>
              <Button component="a" href={`/debts?companyId=${company.id}`} variant="outlined" startIcon={<OpenInNew />}>{t('companies.openDebts')}</Button>
              <Button component="a" href={`/expenses?companyId=${company.id}`} variant="outlined" startIcon={<OpenInNew />}>{t('companies.openExpenses')}</Button>
            </Stack>
          </Paper>
        </Stack>
      )}
      {tab === 1 && <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{t('companies.detailUsers')}</Typography>
        {usersQuery.isLoading && <CircularProgress size={24} />}
        {usersQuery.isError && <Alert severity="error">{errorMessage(usersQuery.error, t('users.loadError'))}</Alert>}
        <Stack divider={<Divider />}>
          {(usersQuery.data || []).map(user => <Stack key={user.id} direction="row" justifyContent="space-between" sx={{ py: 1 }}><Box><Typography>{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}</Typography><Typography variant="caption" color="text.secondary">{user.email || user.username}</Typography></Box><Chip size="small" label={user.role} /> </Stack>)}
        </Stack>
      </Paper>}
      {tab === 2 && <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}><Typography variant="h6">{t('companies.detailSubscription')}</Typography><Typography sx={{ mt: 1 }}>{t('subscription.planCode')}: {subscriptionQuery.data?.planCode || 'LEGACY'}</Typography><Typography>{t('subscription.endsAt')}: {subscriptionQuery.data?.endsAt ? new Date(subscriptionQuery.data.endsAt).toLocaleDateString() : t('subscription.endsAtHelper')}</Typography><Button sx={{ mt: 2 }} variant="outlined" onClick={() => void openSubscription()}>{t('subscription.manage')}</Button></Paper>}
      {tab === 3 && <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}><Typography variant="h6">{t('companies.detailNotes')}</Typography><Typography color="text.secondary" sx={{ mt: 1 }}>{t('companies.notesFromDetail')}</Typography><Button sx={{ mt: 2 }} variant="outlined" onClick={() => setNotesOpen(true)}>{t('companies.addNote')}</Button></Paper>}
      <SubscriptionDialog open={subscriptionOpen} company={company} subscription={subscription} loading={subscriptionQuery.isLoading} saving={false} error={subscriptionError} onClose={() => setSubscriptionOpen(false)} onSubmit={saveSubscription} />
      <CompanyNoteDialog open={notesOpen} company={company} onClose={() => setNotesOpen(false)} onSaved={() => undefined} />
      <DeleteCompanyDialog
        open={deleteOpen}
        company={company}
        onClose={() => { if (!deleteCompanyMutation.isPending) setDeleteOpen(false); }}
        onConfirm={confirmDelete}
        isLoading={deleteCompanyMutation.isPending}
        error={deleteError}
      />
    </Box>
  );
};

export default CompanyDetail;
